/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import './Policies.css';

const EmailLink = <a href="mailto:info@stakmobility.com">info@stakmobility.com</a>;
const StakUrl = (
  <a target="_blank" rel="noreferrer" href="https://www.stakmobility.com">
    https://www.stakmobility.com
  </a>
);

const tableStyles = {
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '10pt',
    marginTop: '8px',
    marginBottom: '8px',
  },
  th: {
    border: '1px solid #999',
    padding: '6px 8px',
    textAlign: 'left',
    verticalAlign: 'top',
    backgroundColor: '#f0f0f0',
  },
  td: {
    border: '1px solid #999',
    padding: '6px 8px',
    textAlign: 'left',
    verticalAlign: 'top',
  },
};

export default function PrivacyPolicyContent() {
  return (
    <div className="policy">
      <h1>Privacy Policy</h1>
      <p>Last Modified: May 2026</p>

      <div className="policy-section">
        <h2>Introduction</h2>
        <p>
          Stak Corp. (<b>&ldquo;Stak,&rdquo;</b> <b>&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo;</b> or <b>&ldquo;us&rdquo;</b>) respects your
          privacy and we are committed to protecting it through our compliance with this policy.
        </p>
        <p>
          This policy (the &ldquo;<b>Privacy Policy</b>&rdquo; or &ldquo;<b>policy</b>&rdquo;) describes: the types of personal
          information Stak collects, processes, and discloses from you via our website located at: {StakUrl}, including its microsites, mobile websites, mobile applications, and your use of
          interactive features, widgets, plug-ins, content, products, software as a service, assets,
          downloads, and/or other services that we own or control or that post a link to our Terms of
          Use (collectively, the &ldquo;<b>Platform</b>&rdquo;), as well as any rights available to you concerning
          your personal information.
        </p>
        <p>
          <b>
            <u>PLEASE READ THIS PRIVACY POLICY CAREFULLY</u>
          </b>{' '}
          BY USING OR ACCESSING THE PLATFORM IN ANY MANNER, YOU ACKNOWLEDGE THAT YOU ACCEPT THE
          PRACTICES AND POLICIES OUTLINED IN THIS PRIVACY POLICY, AND YOU HEREBY CONSENT THAT WE WILL
          COLLECT, USE, AND SHARE YOUR INFORMATION IN THE WAYS DESCRIBED HEREIN. IF YOU DO NOT AGREE
          WITH THIS PRIVACY POLICY, YOU MAY NOT USE THE PLATFORM. IF YOU USE THE PLATFORM ON BEHALF OF
          SOMEONE ELSE, YOU REPRESENT THAT YOU ARE AUTHORIZED BY SUCH INDIVIDUAL TO ACCEPT THIS PRIVACY
          POLICY ON THE INDIVIDUAL&rsquo;S BEHALF.
        </p>
        <p>
          This policy is effective as of the date stated at the top of this page and may change from
          time to time, so please check this policy periodically for updates. Your continued use of
          this Platform after we make changes is deemed to be acceptance of those changes, so please
          check the policy periodically for updates. Any capitalized words not defined herein shall
          take on the meaning as provided under the applicable statute or regulation.
        </p>
      </div>

      <div className="policy-section">
        <h2>I. Information We Collect About You and How We Use It</h2>
        <p>
          This section describes the types of personal information we may collect about you. Such
          information is generally collected either (i) directly from your voluntary engagement with
          the Platform; (ii) automatically from you when you interact with the Platform; or (iii)
          automatically from you when you interact with the Platform through the use of cookies and
          other similar technologies. We collect several types of information from and about users of
          our Platform, including:
        </p>
        <ul>
          <li>
            <b>Identifying Information</b> &ndash; such as name, postal address, e-mail address, telephone
            number, license plate number, driver&rsquo;s license number, and other vehicle information such
            as a vehicle identification number (VIN) the color, or make or model of your vehicle.
          </li>
          <li>
            <b>Financial information</b> &ndash; to process your payment for the services, we may collect
            your credit card number, billing address, bank account number, or other financial
            information to process payment. Note that Stak Corp does not itself process that
            information. Instead, we pass that information on to our third-party payment processors.
          </li>
          <li>
            <b>Location information</b> &ndash; to provide you with the services, we may automatically
            collect information from your device about your physical location.
          </li>
          <li>
            <b>Device Information</b> &ndash; when you interact with our Platform, we may automatically
            collect information about the computers, phones, and other devices you are using (e.g. IP
            address, WiFi information, internet connection, device attributes, operating system,
            browsing type, version, etc.).
          </li>
          <li>
            <b>Platform Usage</b> &ndash; so that we may understand and improve our user experience and
            functionality of the Platform, we may automatically collect information about how you
            interact with the Platform such as mouse movements, clicks, keystrokes, the date of your
            visit, the time spent on pages of the Platform, or other actions.
          </li>
        </ul>
      </div>

      <div className="policy-section">
        <h2>II. How We Share Information We Collect</h2>
        <blockquote>
          <p>
            We are required to disclose how your personal information may be collected, maintained,
            and disclosed in conformance with federal and California state laws that are applicable to
            Stak.
          </p>
          <p>We disclose the following categories of personal information for business purposes:</p>
        </blockquote>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={tableStyles.th}>Category of Personal Data</th>
              <th style={tableStyles.th}>Category of Recipients</th>
              <th style={tableStyles.th}>Business Purpose(s)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tableStyles.td}>Identifying Information</td>
              <td style={tableStyles.td}>
                Employees, contractors, agents, and service providers (including third-party service
                providers) with a legitimate need to use it to provide the Platform (e.g., customer
                support providers, payment processors, technical and infrastructure providers, fraud
                detection providers); government entities (as needed); affiliates, successors, or
                assignees and their advisors in connection with a merger, acquisition, financing,
                reorganization, sale of assets, or similar corporate transaction
              </td>
              <td style={tableStyles.td}>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  <li>Create your account on the Platform to allow you to use our services</li>
                  <li>Provide the Platform and our services, including customer support</li>
                  <li>Detect and prevent fraud and security incidents</li>
                  <li>Process payments</li>
                  <li>Facilitate improvements and technical infrastructure to the Platform and our services</li>
                  <li>Comply with applicable law, respond to valid legal process, and fulfill legal obligations</li>
                  <li>Facilitate corporate transactions, such as mergers, acquisitions, or similar corporate transactions</li>
                  <li>Investigate and enforce potential violations of our Terms of Use, SaaS Agreement, or other policies</li>
                  <li>Protect the rights, property, or safety of you, Stak, or others</li>
                  <li>Resolve legal disputes</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td style={tableStyles.td}>Financial Information</td>
              <td style={tableStyles.td}>
                Payment processors, financial institutions; government entities (as needed)
              </td>
              <td style={tableStyles.td}>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  <li>Process payments</li>
                  <li>Comply with applicable law, respond to valid legal process, and fulfill legal obligations</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td style={tableStyles.td}>Location Information</td>
              <td style={tableStyles.td}>
                Contracts, agents, and service providers (including third-party service providers)
                with a legitimate need to know the location of your vehicle to provide you with the
                services; government entities (as needed)
              </td>
              <td style={tableStyles.td}>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  <li>Detect where your vehicle is to provide you with our services</li>
                  <li>Comply with applicable law, respond to valid legal process, and fulfill legal obligations</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td style={tableStyles.td}>Device Information</td>
              <td style={tableStyles.td}>
                Data analytic providers, internet service providers, social networks; government
                entities (as needed)
              </td>
              <td style={tableStyles.td}>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  <li>Improve the quality of the services provided to you by the Platform</li>
                  <li>Comply with applicable law, respond to valid legal process, and fulfill legal obligations</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
        <blockquote>
          <p>
            <em>
              <b>We do not disclose your personal information for targeted advertisements.</b>
            </em>
          </p>
          <p>
            <em>
              <b>
                We do not disclose personal information in exchange for monetary or other forms of
                consideration.
              </b>
            </em>
          </p>
          <p>
            <em>
              <b>
                At this time, Stak does not collect or disclose automated profiling data in a manner
                that may influence or produce legal or similarly significant effects to you.
              </b>
            </em>
          </p>
        </blockquote>
      </div>

      <div className="policy-section">
        <h2>III. Cookies and Other Tracking Technologies</h2>
        <blockquote>
          <p>
            Personal information that is automatically collected may be obtained through cookies and
            similar technologies. Cookies are small files that are stored on your computer or mobile
            device through a user ID or other preferences. We use cookies to personalize your
            experience when you use the Platform (e.g. auto-populating your log-in credentials or
            settings). You are free to set your browser or operating system settings to limit certain
            tracking or to decline cookies, but by doing so, you may not be able to use certain
            features through our Platform or take full advantage of all our services.
          </p>
        </blockquote>
        <p>
          Note that third-party sites that are linked to the Platform may set cookies or other
          technologies to collect information about your use and interaction with their websites or
          content. We do not have access to, or control, over such third-party data collection.
        </p>
        <p>
          <b>Opt-Out Preference Signals.</b> Residents of California may use opt-out preference
          signals, such as the Global Privacy Control (GPC), to opt out from the &ldquo;sale&rdquo; or &ldquo;sharing&rdquo;
          (as defined by applicable law, rather than the traditional sense) of your personal data.
          Stak currently does not sell or share data. As such, Stak is not required to process opt-out
          preference signals at this time.
        </p>
      </div>

      <div className="policy-section">
        <h2>IV. Security and Retention</h2>
        <blockquote>
          <p>
            Stak has put in place reasonable and appropriate security measures to protect the
            information collected via the Platform. However, note that the confidentiality of
            information transmitted over the Internet cannot be guaranteed. Please exercise caution
            when transmitting information over the Internet and through the Platform. Stak does not
            and cannot guarantee that unauthorized third parties will not gain access to your
            information. It is also very important that you do not share your username or password
            with anyone. You are responsible for all activity that occurs under your account.
          </p>
        </blockquote>
        <p>
          We will keep your personal information as long as reasonably necessary to fulfill the
          purposes for which it was collected. In some circumstances we may retain your personal
          information for longer if and when required (e.g. legal, tax, or accounting requirements).
        </p>
      </div>

      <div className="policy-section">
        <h2>V. Third-Party Websites</h2>
        <blockquote>
          <p>
            The Platform may suggest links to third-party websites that might be useful or of interest
            to you. Stak does not exercise authority over or endorse such third-party websites. In
            each instance, you may access third-party websites at your own risk. Although Stak strives
            to ensure that it refers to reliable third-parties, Stak does not guarantee the accuracy,
            completeness, or timeliness of the information on these third-party websites, nor is Stak
            in any way responsible for any decision you make or action you take in reliance on such
            information. Stak offers you links on an &ldquo;as-is&rdquo; basis and you will no longer be protected
            by the Stak Privacy Policy or security practices. Please read any third-party privacy and
            security policies closely.
          </p>
        </blockquote>
      </div>

      <div className="policy-section">
        <h2>VI. Additional Rights for California Residents</h2>
        <blockquote>
          <p>
            <b>If you are a California resident</b>, certain rights may be accorded to you under the
            California Consumer Privacy Act (CCPA) and its amendments under the California Privacy
            Rights Act (CPRA), including:
          </p>
          <p>
            <b>The Right to Opt-Out of &ldquo;Selling&rdquo; or &ldquo;Sharing&rdquo; Your Personal Data for Targeted
            Advertising.</b>{' '}
            If you do not wish to have your email address used by the Company to promote our own or
            third parties&rsquo; products or services, you can opt-out by emailing us at {EmailLink}.
          </p>
        </blockquote>
        <p>
          <b>The Right to Access.</b> Up to twice each year, you may request, and we will disclose,
          the categories and specific pieces of information we have about you.
        </p>
        <p>
          <b>The Right to Delete.</b> You may request that we delete any personal data we have about
          you.
        </p>
        <p>
          <b>The Right to Correct.</b> You may request, that we correct any incorrect information we
          have about you.
        </p>
        <p>
          <b>The Right to Transfer.</b> You have the right to receive your personal data in a
          structured, commonly used and machine-readable format and, if technically feasible, to have
          it transmitted to another processor.
        </p>
        <p>
          <b>The Right to No Discrimination</b>. You have the right not to be discriminated against
          for exercising the rights described above.
        </p>
        <p>
          <b>Exercising Your Rights.</b> Aside from specific request mechanisms explained above, you
          may exercise the privacy rights available to you by emailing {EmailLink}. We may be unable
          to delete your personal information except by also deleting your user account. We may not
          accommodate a request to change information if we believe the change would violate any law
          or legal requirement or cause the information to be incorrect
        </p>
        <p>
          <b>The Right to Appeal.</b> You may appeal our decision to decline your requests by emailing
          us at {EmailLink} and we will make a decision within sixty (60) days of your appeal. If we
          deny your appeal, you may submit a complaint regarding the matter to the applicable U.S.
          state Attorney General.
        </p>
      </div>

      <div className="policy-section">
        <h2>VII. Children Under the Age of 16</h2>
        <blockquote>
          <p>
            In accordance with the Children&rsquo;s Online Privacy Protection Act of 1998, Stak does not
            knowingly request information from anyone under the age of 16 without parental consent.
          </p>
        </blockquote>
        <p>
          Our Platform is not intended for children under 16 years of age. No one under age 16 may
          provide any information to or on the Platform. If you are under 16, do not use or provide
          any information on this Platform or on or through any of its features/register on the
          Platform, make any purchases through the Platform, use any of the interactive or public
          comment features of this Platform or provide any information about yourself to us, including
          your name, address, telephone number, e-mail address or any screen name or user name you may
          use. If we learn we have collected or received personal information from a child under 16
          without verification of parental consent, we will delete that information. If you believe we
          might have any information from or about a child under 16, please contact us at {EmailLink}.
        </p>
      </div>

      <div className="policy-section">
        <h2>VIII. Contact Information</h2>
        <p>
          To ask questions or comment about this Privacy Policy and our privacy practices described
          herein, contact us at: {EmailLink}.
        </p>
      </div>
    </div>
  );
}
